import React from 'react';
import { Container, Row, Col, Card, Badge } from 'react-bootstrap';
import { motion } from 'framer-motion';
import {
  FaHtml5, FaCss3Alt, FaJs, FaReact, FaBootstrap,
  FaPython, FaDatabase, FaMarkdown, FaBlog, FaChartBar,
  FaCamera, FaNetworkWired, FaTools, FaCode, FaLaptopCode, FaServer, FaPencilAlt
} from 'react-icons/fa';

const Skills = ({ currentTheme }) => {
  const skillCategories = [
    {
      id: 'frontend',
      title: 'Frontend Development',
      icon: FaLaptopCode,
      skills: [
        { name: 'HTML5', icon: FaHtml5, level: 'Expert', color: '#E34F26' },
        { name: 'CSS3', icon: FaCss3Alt, level: 'Expert', color: '#1572B6' },
        { name: 'JavaScript', icon: FaJs, level: 'Expert', color: '#F7DF1E' },
        { name: 'React.js', icon: FaReact, level: 'Expert', color: '#61DAFB' },
        { name: 'Bootstrap', icon: FaBootstrap, level: 'Expert', color: '#7952B3' },
        { name: 'Responsive Design', icon: FaCode, level: 'Expert', color: '#38B2AC' },
        { name: 'Web Performance', icon: FaTools, level: 'Advanced', color: '#4A5568' }
      ]
    },
    {
      id: 'data',
      title: 'Data Science',
      icon: FaChartBar,
      skills: [
        { name: 'Python', icon: FaPython, level: 'Expert', color: '#3776AB' },
        { name: 'Data Analysis', icon: FaChartBar, level: 'Advanced', color: '#38B2AC' },
        { name: 'Machine Learning', icon: FaDatabase, level: 'Advanced', color: '#9C27B0' },
        { name: 'Data Visualization', icon: FaChartBar, level: 'Expert', color: '#FF6384' },
        { name: 'Statistical Analysis', icon: FaChartBar, level: 'Advanced', color: '#4BC0C0' }
      ]
    },
    {
      id: 'writing',
      title: 'Technical Writing',
      icon: FaPencilAlt,
      skills: [
        { name: 'Documentation', icon: FaMarkdown, level: 'Expert', color: '#000000' },
        { name: 'Blog Writing', icon: FaBlog, level: 'Expert', color: '#FF5722' },
        { name: 'API Documentation', icon: FaCode, level: 'Advanced', color: '#1976D2' },
        { name: 'Technical Guides', icon: FaPencilAlt, level: 'Expert', color: '#388E3C' }
      ]
    },
    {
      id: 'cctv',
      title: 'CCTV Systems',
      icon: FaCamera,
      skills: [
        { name: 'System Design', icon: FaCamera, level: 'Expert', color: '#607D8B' },
        { name: 'Network Setup', icon: FaNetworkWired, level: 'Expert', color: '#0288D1' },
        { name: 'Security Protocols', icon: FaServer, level: 'Advanced', color: '#D32F2F' },
        { name: 'Maintenance', icon: FaTools, level: 'Expert', color: '#388E3C' }
      ]
    }
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

  return (
    <Container fluid className="py-5 position-relative overflow-hidden" id='skills' style={{ backgroundColor: currentTheme.background }}>
      <div className="position-absolute w-100 h-100 top-0 start-0" style={{ zIndex: -1 }}>
        <motion.div
          animate={{
            background: [
              `linear-gradient(45deg, ${currentTheme.primary}20 0%, ${currentTheme.secondary}20 100%)`,
              `linear-gradient(45deg, ${currentTheme.secondary}20 0%, ${currentTheme.primary}20 100%)`
            ],
          }}
          transition={{ duration: 3, repeat: Infinity, repeatType: 'reverse' }}
          className="w-100 h-100"
        />
      </div>

      <Container className="position-relative">
        <motion.div {...fadeInUp}>
          <Row className="mb-5">
            <Col className="text-center">
              <h2 className="display-4 fw-bold" style={{ color: currentTheme.primary }}>Skills & Technologies</h2>
              <div className="d-flex justify-content-center mb-4">
                <div className="border-bottom border-3" style={{ width: '50px', borderColor: currentTheme.primary }}></div>
              </div>
            </Col>
          </Row>

          <Row className="g-4">
            {skillCategories.map((category, index) => (
              <Col lg={6} md={12} key={index}>
                <motion.div
                  whileHover={{ y: -5 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="border-0 shadow-sm h-100" style={{ backgroundColor: currentTheme.background === '#121212' ? '#1E1E1E' : '#FFFFFF' }}>
                    <Card.Body>
                      <div className="d-flex align-items-center gap-3 mb-4">
                        <div className="p-3 rounded-circle" style={{ backgroundColor: currentTheme.primary }}>
                          <category.icon size={24} style={{ color: '#FFFFFF' }} />
                        </div>
                        <h3 className="h4 mb-0" style={{ color: currentTheme.text }}>{category.title}</h3>
                      </div>
                      <Row className="g-3">
                        {category.skills.map((skill, idx) => (
                          <Col lg={6} md={6} sm={12} key={idx}>
                            <motion.div
                              whileHover={{ scale: 1.05 }}
                              className="d-flex align-items-center gap-3 p-3 rounded-3"
                              style={{ backgroundColor: currentTheme.background === '#121212' ? '#2C2C2C' : '#F5F7FA' }}
                            >
                              <skill.icon size={20} style={{ color: skill.color }} />
                              <div>
                                <h6 className="mb-0" style={{ color: currentTheme.text }}>{skill.name}</h6>
                                <small className="text-muted">{skill.level}</small>
                              </div>
                            </motion.div>
                          </Col>
                        ))}
                      </Row>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>

          <Row className="mt-5">
            <Col lg={12}>
              <motion.div
                className="p-4 rounded-3 text-center shadow-sm"
                whileHover={{ scale: 1.02 }}
                style={{ backgroundColor: currentTheme.background === '#121212' ? '#1E1E1E' : '#FFFFFF' }}
              >
                <h4 className="mb-3" style={{ color: currentTheme.primary }}>Additional Tools & Technologies</h4>
                <div className="d-flex flex-wrap justify-content-center gap-2">
                  {['Git', 'Docker', 'AWS', 'Node.js', 'MongoDB', 'SQL', 'Redux', 'TypeScript',
                    'Webpack', 'Jest', 'CI/CD', 'Agile'].map((tool, index) => (
                    <motion.div
                      key={index}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Badge
                        className="px-3 py-2 m-1 shadow-sm"
                        style={{ cursor: 'pointer', backgroundColor: currentTheme.secondary, color: '#FFFFFF' }}
                      >
                        {tool}
                      </Badge>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </Col>
          </Row>
        </motion.div>

        <motion.div
          animate={{
            rotate: 360,
            transition: { duration: 20, repeat: Infinity, ease: "linear" }
          }}
          className="position-absolute"
          style={{ top: '10%', right: '5%', opacity: 0.1 }}
        >
          <FaCode style={{ fontSize: '4rem', color: currentTheme.primary }} />
        </motion.div>
      </Container>
    </Container>
  );
};

export default Skills;